/**
 * Nukes (§1.8): ring effects on pop / radiation / structures / cities, ship kills,
 * per-observer detonation events. Braced cities (§1.15) lose half the pop around them and no
 * levels; capitals cannot drop below L1 before capitals are exposed, and from 10:00 the first
 * lethal blow leaves them critical instead (§1.11, systems/capital.ts).
 */
import { BALANCE } from '../balance.js';
import { allied, capitalOwnerAt, isShip, removeEntity, setCityLevel } from '../entities.js';
import { axialDist, axialOfPoint, landRing } from '../hex.js';
import type { CityEntity, HexIdx, MatchState, SimEvent } from '../types.js';
import type { Blast } from './missiles.js';
import { capitalHitLevel } from './capital.js';
import { detonationViews } from './vision.js';

const K = BALANCE.nukes;

export function nukesSystem(state: MatchState, events: SimEvent[], blasts: Blast[]): void {
  for (const b of blasts) detonate(state, b, events);
}

/** Braced cities within reach of a blast centred on axial (q, r). */
function bracedNear(state: MatchState, q: number, r: number): CityEntity[] {
  const out: CityEntity[] = [];
  const reach = K.popKill.length - 1 + BALANCE.brace.radius;
  const w = state.world;
  for (const e of state.entities.values()) {
    if (e.kind === 'city' && state.tick < e.braceUntil && axialDist(w.q[e.hex], w.r[e.hex], q, r) <= reach) out.push(e);
  }
  return out;
}

export function detonate(state: MatchState, b: Blast, events: SimEvent[]): void {
  events.push({ type: 'detonation', owner: b.owner, kind: b.kind, x: b.x, y: b.y, tick: state.tick, views: detonationViews(state, b.x, b.y) });
  const shooter = b.owner > 0 ? state.stats.players[b.owner - 1] : null;
  if (shooter) shooter.hits++;
  const w = state.world;
  const [q, r] = axialOfPoint(state.geo, b.x, b.y);
  const braced = bracedNear(state, q, r);
  const shielded = (h: HexIdx) => braced.some((c) => axialDist(w.q[c.hex], w.r[c.hex], w.q[h], w.r[h]) <= BALANCE.brace.radius);
  const ring: HexIdx[] = [];
  for (let k = 0; k < K.popKill.length; k++) {
    ring.length = 0;
    landRing(state.geo, q, r, k, ring);
    for (const h of ring) {
      const kill = K.popKill[k] * (braced.length && shielded(h) ? BALANCE.brace.popKillMult : 1);
      const killed = state.pop[h] * kill;
      state.pop[h] -= killed;
      const victim = state.owner[h];
      if (shooter && victim > 0 && !allied(state, b.owner, victim)) shooter.popKilled += killed;
      state.rad[h] = Math.min(1, state.rad[h] + K.rad[k]);
      const sid = state.structId[h];
      if (sid < 0) continue;
      const e = state.entities.get(sid)!;
      if (e.kind === 'city' && e.buildDoneTick <= state.tick) {
        if (K.cityLevelLoss[k] === 0) continue;
        if (state.tick < e.braceUntil) {
          if (e.owner > 0) state.stats.players[e.owner - 1].bracedHits++;
          continue;
        }
        let lvl = e.level - K.cityLevelLoss[k];
        // capitals: hardened before 10:00, a second life after (critical)
        if (e.owner > 0 && capitalOwnerAt(state, h) === e.owner) lvl = capitalHitLevel(state, state.players[e.owner - 1], e, lvl, events);
        if (lvl <= 0) {
          if (e.owner > 0) state.stats.players[e.owner - 1].citiesLost++;
          removeEntity(state, e.id, 'destroyed', events);
        } else if (lvl !== e.level) setCityLevel(state, e, lvl);
      } else if (k === 0 || (k === 1 && e.kind !== 'silo')) {
        removeEntity(state, e.id, 'destroyed', events);
      }
    }
  }
  const r2 = K.shipKillRadius ** 2;
  const dead: number[] = [];
  for (const e of state.entities.values()) {
    if (isShip(e) && (e.x - b.x) ** 2 + (e.y - b.y) ** 2 <= r2) dead.push(e.id);
  }
  for (const id of dead) removeEntity(state, id, 'destroyed', events);
}
